import { cx } from "@emotion/css";
import { faMap, faMountain, faStar, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "maplibre-gl/dist/maplibre-gl.css";
import { memo, useRef } from "react";
import { Badge, Button, Card, OverlayTrigger, Stack, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import type { CampsiteDataFragment, CampsiteTraitDistanceDataFragment } from "../types/graphql/graphql";
import { getEnvAttributes } from "./CampsiteCard";
import styles from "./CampsiteCard.module.css";

export type CampsitePreviewCardProps = {
  campsite: CampsiteDataFragment;
  onExpand?: (ref: string) => void;
  onToggleFavorite?: (campsiteId: number) => void;
  isFavorite?: boolean;
};

export const CampsitePreviewCard = memo(function CampsitePreviewCard({
  campsite: { id, ref, name, zip, place, canton, maxHeadcount, altitude, area, traits },
  onExpand,
  isFavorite,
  onToggleFavorite,
}: CampsitePreviewCardProps) {
  const { t } = useTranslation();
  const envAttributes = getEnvAttributes(traits, t);
  const appliedEnvironments = Object.values(envAttributes).filter(({ trait }) => trait.applies);
  const elementRef = useRef<HTMLDivElement>(null);

  return (
    <Card key={id} ref={elementRef} className={cx(styles.card, "bg-light", "border-0")}>
      <Card.Body className="pb-3">
        <Card.Title className="d-flex justify-content-between m-0 pt-1 align-items-center gap-3 pb-3">
          <div className="d-flex flex-grow-1 flex-wrap align-items-center">
            <Button
              variant="link"
              className="p-0 text-decoration-none text-start lh-sm me-3 text-break block font-display fs-3"
              onClick={() => onExpand?.(ref)}
            >
              {name}
            </Button>
            <div className="text-sm">
              {t("public.campsites.campsite.in", { zip, place, canton: canton?.toUpperCase() })}
            </div>
          </div>
          <div className="d-flex ms-3 flex-wrap align-items-center gap-2 justify-content-end">
            <button
              type="button"
              className="badge border-0 bg-secondary text-primary text-decoration-none"
              onClick={() => onExpand?.(ref)}
            >
              {ref}
            </button>
            {onToggleFavorite && (
              <Button className={cx(styles.favToggle, "p-0")} variant="link" onClick={() => onToggleFavorite(id)}>
                <FontAwesomeIcon color={isFavorite ? "var(--bs-yellow)" : undefined} icon={faStar} size="lg" />
              </Button>
            )}
          </div>
        </Card.Title>
        <Stack gap={2}>
          <Stack direction="horizontal" gap={2} style={{ flexWrap: "wrap" }}>
            {!!maxHeadcount && (
              <OverlayTrigger
                trigger={["hover", "focus"]}
                overlay={<Tooltip>{t("activemodel.attributes.campsite/filter.headcount")}</Tooltip>}
              >
                <Badge pill className="bg-secondary text-primary">
                  <FontAwesomeIcon icon={faUser} className="me-1" />
                  {maxHeadcount}
                </Badge>
              </OverlayTrigger>
            )}
            {!!altitude && (
              <Badge pill className="bg-secondary text-primary">
                <FontAwesomeIcon icon={faMountain} className="me-1" />
                {t("format.altitude", { number: altitude })}
              </Badge>
            )}
            {!!area && (
              <Badge className="bg-secondary text-primary" pill>
                <FontAwesomeIcon icon={faMap} className="me-1" />
                <span>
                  {area} m<sup>2</sup>
                </span>
              </Badge>
            )}
            {appliedEnvironments.map(({ icon, label, trait }) => (
              <OverlayTrigger
                show={trait.remarks ? undefined : false}
                trigger={["hover", "focus"]}
                key={label}
                overlay={<Tooltip>{trait.remarks}</Tooltip>}
              >
                <Badge pill className="bg-secondary text-primary">
                  <FontAwesomeIcon icon={icon} className="me-1" />
                  {label}{" "}
                  {(trait as CampsiteTraitDistanceDataFragment).value
                    ? `(${(trait as CampsiteTraitDistanceDataFragment).value} m)`
                    : ""}
                </Badge>
              </OverlayTrigger>
            ))}
          </Stack>
        </Stack>
      </Card.Body>
    </Card>
  );
});
